import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import Modal from '../../../../ui/Model';
import Button, { ButtonType } from '../../core-ui/button/button.component';
import { birdToast } from '../../core-ui/toast';
import { isErrorInfo } from '../../../interfaces/error-info.interface';
import {
  CreateTicketPayload,
  TicketCategoriesModel,
  TicketPriority,
  TicketSegment
} from '../../../models/ticket.model';
import { TicketUiModel } from '../../../ui-models/ticket.ui-model';
import { createTicket, getTicketCategories } from '../../../services/data/ticket.data';
import {
  attachmentProblem,
  CategoryGroup,
  CategoryOption,
  categoryGroups,
  creatablePriorities,
  emergencyChoice
} from '../../../services/helpers/ticket-rules.helper';
import { encodeTicketFiles } from '../../../services/helpers/ticket-files.helper';
import { TicketFilesInput } from './ticket-files-input.component';

/** What the page raising the ticket already knows. All optional. */
export interface RaiseTicketContext {
  reservationRef?: string;
  unitRef?: string;
  contactRef?: string;
  customerEmail?: string;
  /** Who the ticket can be about from this page: a reservation is a guest's. */
  segments?: TicketSegment[];
  /** Shown under the title, e.g. "Reservation BN-1234 · Zamalek 2B". */
  description?: string;
}

interface Props {
  show: boolean;
  context?: RaiseTicketContext;
  onClose: () => void;
  onCreated?: (ticket: TicketUiModel) => void;
}

/** Staff-to-staff requests: no guest / owner is the customer, so no email field. */
const INTERNAL_SEGMENTS: TicketSegment[] = ['internal_sales_acq', 'internal_finance'];
const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';
const INPUT = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm';
const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

/**
 * Raise a ticket from the CX taxonomy. The category decides the first owner team,
 * the default priority and whether it is an emergency, so the form asks for the
 * category first and offers only what that category allows.
 *
 * The person raising it often cannot open it afterwards. If the first owner team
 * is not theirs, Zammad gives them `create` on that group but not `read`. So the
 * confirmation names the ticket and its team and doesn't open it.
 */
export const RaiseTicketModal: React.FC<Props> = ({ show, context, onClose, onCreated }) => {
  const [doc, setDoc] = useState<TicketCategoriesModel | undefined>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [category, setCategory] = useState<CategoryOption | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<TicketPriority | undefined>();
  const [emergency, setEmergency] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || doc) return;
    getTicketCategories().then((result) => {
      if (isErrorInfo(result)) setLoadFailed(true);
      else setDoc(result);
    });
  }, [show, doc]);

  // A fresh form every time it opens: a half-written ticket from another page is not this one.
  useEffect(() => {
    if (!show) return;
    setCategory(null);
    setTitle('');
    setBody('');
    setPriority(undefined);
    setEmergency(false);
    setCustomerEmail(context?.customerEmail ?? '');
    setFiles([]);
    // Only on open: callers pass `context` as an inline object, which is new on
    // every render and would otherwise wipe the form while someone is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const segmentsKey = (context?.segments ?? []).join(',');
  const groups: CategoryGroup[] = useMemo(
    () => (doc ? categoryGroups(doc, segmentsKey ? segmentsKey.split(',') : undefined) : []),
    [doc, segmentsKey]
  );

  const item = category?.item;
  const priorities = item ? creatablePriorities(item.priority) : [];
  const emergencyRule = item ? emergencyChoice(item.emergency) : undefined;
  const firstTeam = item?.ownerChain[0]?.teams[0];
  const filesProblem = attachmentProblem(files);
  const canSubmit = Boolean(item && title.trim() && body.trim() && !filesProblem && !saving);

  const submit = async () => {
    if (!item) return;
    setSaving(true);
    try {
      const payload: CreateTicketPayload = {
        code: item.code,
        title: title.trim(),
        body: body.trim(),
        priority: priority && priority !== item.priority ? priority : undefined,
        emergency: emergencyRule?.editable ? emergency : undefined,
        reservationRef: context?.reservationRef,
        unitRef: context?.unitRef,
        contactRef: context?.contactRef,
        customerEmail: customerEmail.trim() || undefined,
        attachments: files.length ? await encodeTicketFiles(files) : undefined
      };
      const result = await createTicket(payload);
      if (isErrorInfo(result)) {
        birdToast.error(
          result.status === 403
            ? 'You do not have permission to raise tickets'
            : 'The ticket was not raised. Try again.'
        );
        return;
      }
      birdToast.success(`Ticket #${result.number} raised to ${result.team}`);
      onCreated?.(result);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[88vh] lg:w-[760px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          Raise ticket
        </div>
      }
      footerContent={
        <div className="flex items-center justify-end gap-4">
          {firstTeam ? (
            <span className="mr-auto text-xs text-neutral-300">Goes to {firstTeam}</span>
          ) : null}
          <Button type={ButtonType.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button type={ButtonType.primary} disabled={!canSubmit} loading={saving} onClick={submit}>
            Raise ticket
          </Button>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {context?.description ? (
          <p className="rounded-lg bg-creams-100 px-3 py-2 text-sm text-neutral-400">
            {context.description}
          </p>
        ) : null}

        {loadFailed ? (
          <p className="text-sm text-secondary-700">
            The ticket categories could not be loaded. Close this and try again.
          </p>
        ) : null}

        <div>
          <label className={LABEL}>Category</label>
          <Select
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectMenuPortalStyles}
            classNamePrefix="react-select"
            isLoading={!doc && !loadFailed}
            options={groups}
            placeholder="What is it about?"
            value={category}
            onChange={(option: ValueType<CategoryOption, false>) => {
              const next = (option as CategoryOption) || null;
              setCategory(next);
              setPriority(next?.item.priority);
              setEmergency(next ? emergencyChoice(next.item.emergency).value : false);
            }}
          />
          {item?.handling ? <p className="mt-1 text-xs text-neutral-300">{item.handling}</p> : null}
        </div>

        <div>
          <label className={LABEL}>Title</label>
          <input
            className={INPUT}
            maxLength={200}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="One line the next team will understand"
          />
        </div>

        <div>
          <label className={LABEL}>What happened</label>
          <textarea
            className={`${INPUT} min-h-[120px]`}
            maxLength={20000}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="What was asked, what was already tried, what is needed"
          />
        </div>

        {item ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={LABEL}>Priority</label>
              <select
                className={INPUT}
                value={priority}
                onChange={(event) => setPriority(event.target.value as TicketPriority)}
              >
                {priorities.map((p: TicketPriority) => (
                  <option key={p} value={p}>
                    {doc?.priorities[p] ?? p}
                    {p === item.priority ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-neutral-400">
                <input
                  type="checkbox"
                  checked={emergency}
                  disabled={!emergencyRule?.editable}
                  onChange={(event) => setEmergency(event.target.checked)}
                />
                Emergency
                {emergencyRule && !emergencyRule.editable ? (
                  <span className="text-xs text-neutral-300">(set by the category)</span>
                ) : null}
              </label>
            </div>
          </div>
        ) : null}

        {item && !INTERNAL_SEGMENTS.includes(item.segment) ? (
          <div>
            <label className={LABEL}>Guest / owner email (optional)</label>
            <input
              className={INPUT}
              type="email"
              value={customerEmail}
              onChange={(event) => setCustomerEmail(event.target.value)}
              placeholder="Who the ticket is about"
            />
          </div>
        ) : null}

        <TicketFilesInput files={files} onChange={setFiles} disabled={saving} />
      </div>
    </Modal>
  );
};
